/**
 * @fileoverview Response helpers for this feature's mock endpoints.
 * @description Builds the backend's success and error envelopes, and reads query params, for the
 * user-invitation and user-management mocks. Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { AxiosRequestConfig } from 'axios'

/**
 * @description Wraps a payload in the backend's success envelope.
 * @param {unknown} data - The payload.
 * @param {{ page: number; limit: number; total: number }} [meta] - Pagination, for lists.
 * @returns {object} The envelope.
 */
export function ok(data: unknown, meta?: { page: number; limit: number; total: number }) {
  return {
    httpStatus: 200,
    message: 'OK',
    traceId: crypto.randomUUID(),
    data,
    ...(meta && { meta }),
  }
}

/**
 * @description Builds the backend's error envelope.
 * @param {number} httpStatus - The HTTP status.
 * @param {string} appCode - The application error code.
 * @param {string} message - The message.
 * @returns {[number, object]} The mock reply.
 */
export function fail(httpStatus: number, appCode: string, message: string): [number, object] {
  return [
    httpStatus,
    { httpStatus, httpError: '', appCode, message, traceId: crypto.randomUUID(), data: [] },
  ]
}

/**
 * @description Reads a request's query params.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {Record<string, string | undefined>} The params.
 */
export function queryParams(config: AxiosRequestConfig): Record<string, string | undefined> {
  return (config.params ?? {}) as Record<string, string | undefined>
}

/**
 * @description Reads a numeric query param with a default.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {string} name - The param name.
 * @param {number} fallback - The default.
 * @returns {number} The value.
 */
export function numberParam(config: AxiosRequestConfig, name: string, fallback: number): number {
  const value = Number(queryParams(config)[name])
  return Number.isFinite(value) && value > 0 ? value : fallback
}

/**
 * @description Replies with one page of a list, in the backend's paginated envelope.
 * @param {AxiosRequestConfig} config - The request config (its page and limit params).
 * @param {T[]} all - Every item.
 * @param {(items: T[]) => unknown} [wrap] - Shapes the page's items into the envelope's `data`
 * (e.g. `{ kind, items }` for an options endpoint); the bare array by default.
 * @returns {[number, object]} The mock reply.
 */
export function page<T>(
  config: AxiosRequestConfig,
  all: T[],
  wrap: (items: T[]) => unknown = (items) => items,
): [number, object] {
  const current = numberParam(config, 'page', 1)
  const limit = Math.min(100, numberParam(config, 'limit', 20))
  const items = all.slice((current - 1) * limit, current * limit)
  return [200, ok(wrap(items), { page: current, limit, total: all.length })]
}
